// Altersgruppen für „Für mein Kind" — der Vorfilter für die Kursauswahl.
//
// Anlass (Rückmeldung von der Matte, 29.09.2026): Jemand meldete sein Kind für
// MMA an. Am Kurs stand „9–14 Jahre", das Kind war 7 — und musste vor Ort
// wieder aus dem Kurs genommen werden. Das Formular hatte die Sportart zuerst
// abgefragt und die Altersgruppe erst danach, als Dropdown, in dem bei nur
// einem Treffer schon etwas vorausgewählt stand. Überlesen ist da leicht.
//
// Jetzt läuft es andersherum: **erst die Altersgruppe, dann die Kurse.** Zur
// Auswahl stehen nur Kurse, die für dieses Alter gedacht sind.
//
// Die ersten fünf Gruppen folgen den Jiu-Jitsu-Kursen (Fit Kids, Budo Minis,
// Jiu Minis, Jiu Kids, Jiu Teens). Sie stehen bewusst hier und nicht in der
// Datenbank: Sie sind der Vorfilter der Eltern-Ansicht, keine Eigenschaft
// eines Kurses.
//
// ERWACHSENENKURSE FÜR JUGENDLICHE (Entscheidung von der Matte, 30.09.2026).
// Maßgeblich ist das Mindestalter, das im CRM am Kurs steht — „KBF Erwachsene
// ab 12", „Kung Fu Erwachsene ab 14", „Jiu Erwachsene ab 15". Ein Kind sieht
// einen Erwachsenenkurs, wenn dieses Mindestalter zu seinem Alter passt; ein
// Erwachsenenkurs ganz ohne Mindestalter ist für Kinder nie zulässig, denn dann
// ließe sich nicht sagen, ob ein Minderjähriger hinein darf.
//   * Die Gruppe „15–17" (`adults`) zeigt alle Erwachsenenkurse, die für sie in
//     Frage kommen.
//   * Die jüngeren Gruppen zeigen nur die mit einem Mindestalter unter 15. Sonst
//     tauchten bei „12–15" Sportarten auf, in denen nur 15-Jährige mitmachen.
//   * Die 15 steht bewusst in zwei Gruppen: 15-Jährige können noch in die
//     Teenkurse (12–15) oder schon in die Erwachsenenkurse (15–17). Und wo ein
//     Kinderkurs vor 15 endet (MMA bis 14), weicht die Kursauswahl für
//     15-Jährige selbst auf den Erwachsenenkurs aus.
// Weil die Jugendlichen noch nicht volljährig sind, müssen die Eltern den
// Vertrag unterschreiben und mitkommen. Das gilt für jeden Erwachsenenkurs, in
// den ein Kind geht — auch für den mit Mindestalter 12.
//
// KINDER- ODER ERWACHSENENKURS. Passt zum Alter beides (KBF Kids bis 13 und
// KBF Erwachsene ab 12, ein 12-Jähriger), geht der Kinderkurs voran — er ist
// für dieses Alter gedacht. Der Erwachsenenkurs kommt als `alternative` mit,
// und das Formular lässt die Eltern zwischen beiden wählen. Nichts wird
// stillschweigend weggelassen, und nichts stillschweigend gesetzt, wo zwei
// Kurse gleich gut passen.
//
// GRENZFÄLLE. Nicht jeder Kurs richtet sich nach diesen Gruppen — „MMA Kids +
// Teens" geht von 9 bis 14, „Judo Minis" nur von 6 bis 7. Streng ausblenden
// wäre falsch (dann wäre MMA für 12- bis 14-Jährige online nicht mehr
// buchbar), einfach anbieten auch (dann landet eine 15-Jährige in einem Kurs
// bis 14). Deshalb unterscheidet `chooseCourse` drei Fälle:
//
//   * Ein Kurs deckt die gewählte Gruppe **ganz** ab → passt für jedes Kind der
//     Gruppe. Er wird gesetzt, es muss nichts mehr gefragt werden.
//   * Kein Kurs der Sportart deckt sie ganz ab → ob einer passt, hängt vom
//     **genauen Alter** ab. Dann wird das Alter abgefragt (ein Tipp auf 12, 13,
//     14 oder 15) und der Kurs daraus bestimmt — die Eltern müssen keinen
//     Altersbereich mit dem ihres Kindes abgleichen.
//   * Zum genauen Alter passt kein Kurs → es gibt keinen; dann bleibt der
//     Rückruf, statt das Kind in einen unpassenden Kurs zu schicken.
//
// Ein nur teilweise passender Kurs wird so nie ausgesucht, ohne dass das Alter
// feststeht.

export interface AgeGroup {
  min: number;
  max: number;
  /** Auch Erwachsenenkurse kommen in Frage; die Eltern müssen mitkommen. */
  adults?: boolean;
}

/**
 * Ab diesem Alter beginnt die Gruppe der älteren Jugendlichen („15–17"), für
 * die Erwachsenenkurse regulär zur Wahl stehen. Wer jünger ist, sieht einen
 * Erwachsenenkurs nur, wo das CRM ein niedrigeres Mindestalter nennt.
 */
export const ADULT_GROUP_FROM_AGE = 15;

/** Die Altersgruppen für Kinder und Jugendliche, jung nach alt. */
export const CHILD_AGE_GROUPS: readonly AgeGroup[] = [
  { min: 2, max: 3 },
  { min: 4, max: 5 },
  { min: 6, max: 8 },
  { min: 9, max: 11 },
  { min: 12, max: 15 },
  { min: ADULT_GROUP_FROM_AGE, max: 17, adults: true },
];

/** Der Altersbereich eines Kurses; `null` = offen (kein Mindest-/Höchstalter). */
export interface CourseAgeRange {
  age_min: number | null;
  age_max: number | null;
  /** true = Erwachsenenkurs, false = Kinder-/Jugendkurs. */
  for_adults?: boolean | null;
}

export const sameGroup = (a: AgeGroup | null, b: AgeGroup | null): boolean =>
  !!a && !!b && a.min === b.min && a.max === b.max;

/** „9–11 Jahre" bzw. „ab 12 Jahren" — so steht es im Formular. */
export function formatAgeRange(min: number | null, max: number | null): string {
  if (min != null && max != null) return min === max ? `${min} Jahre` : `${min}–${max} Jahre`;
  if (min != null) return `ab ${min} Jahren`;
  if (max != null) return `bis ${max} Jahre`;
  return '';
}

/**
 * „zwischen 9 und 11 Jahre alt" — für den Satz „… wenn dein Kind am Tag des
 * Probetrainings … ist".
 */
export function ageRuleText(min: number | null, max: number | null): string {
  if (min != null && max != null) {
    return min === max ? `genau ${min} Jahre alt` : `zwischen ${min} und ${max} Jahre alt`;
  }
  if (min != null) return `mindestens ${min} Jahre alt`;
  if (max != null) return `höchstens ${max} Jahre alt`;
  return '';
}

/** Kurz für die Knöpfe: „9–11". */
export const groupLabel = (g: AgeGroup): string => `${g.min}–${g.max}`;

/** Alle Jahre einer Gruppe: 12–15 → [12, 13, 14, 15]. */
export const agesOf = (g: AgeGroup): number[] =>
  Array.from({ length: g.max - g.min + 1 }, (_, i) => g.min + i);

/**
 * Deckt der Kurs die Altersgruppe **vollständig** ab? Dann passt jedes Kind
 * aus dieser Gruppe hinein, egal ob es 9 ist oder 11.
 */
export function coversGroup(course: CourseAgeRange, group: AgeGroup): boolean {
  const fromOk = course.age_min == null || course.age_min <= group.min;
  const toOk = course.age_max == null || course.age_max >= group.max;
  return fromOk && toOk;
}

/**
 * Überschneidet sich der Kurs mit der Altersgruppe? Dann passen zumindest
 * *manche* Kinder aus der Gruppe hinein (z. B. „MMA 9–14" bei „12–15": die 12-
 * bis 14-Jährigen ja, die 15-Jährigen nicht).
 */
export function overlapsGroup(course: CourseAgeRange, group: AgeGroup): boolean {
  const startsInTime = course.age_min == null || course.age_min <= group.max;
  const endsInTime = course.age_max == null || course.age_max >= group.min;
  return startsInTime && endsInTime;
}

/** Ist ein Kind dieses Alters im Kurs richtig? Grenzen sind eingeschlossen. */
export function containsAge(course: CourseAgeRange, age: number): boolean {
  return (course.age_min == null || course.age_min <= age) && (course.age_max == null || course.age_max >= age);
}

/** Erwachsenenkurs, in den ein Kind hinein darf: das CRM nennt ein Mindestalter. */
const isAdultCourseWithMinAge = (course: CourseAgeRange): boolean =>
  course.for_adults === true && course.age_min != null;

/**
 * Steht der Kurs für diese Gruppe zur Wahl? Er muss sich mit ihr überschneiden.
 * Kinder- und Jugendkurse kommen für jede Gruppe in Frage. Erwachsenenkurse nur
 * mit ausdrücklichem Mindestalter — und für die Gruppe der älteren Jugendlichen
 * (`adults`) alle, für jüngere nur die mit Mindestalter unter 15 (siehe oben).
 */
export function isOfferedTo(course: CourseAgeRange, group: AgeGroup): boolean {
  if (!overlapsGroup(course, group)) return false;
  if (course.for_adults === false) return true;
  if (!isAdultCourseWithMinAge(course)) return false;
  return group.adults === true || (course.age_min as number) < ADULT_GROUP_FROM_AGE;
}

/**
 * Wie weit der Kurs gefasst ist (kleiner = passgenauer). Ein Kurs „9–11" ist
 * für die Gruppe 9–11 die bessere Wahl als „9–15". Offene Grenzen zählen als
 * sehr weit.
 */
const courseSpan = (course: CourseAgeRange): number =>
  (course.age_max ?? 99) - (course.age_min ?? 0);

/** Von mehreren Kursen der passgenaueste; bei Gleichstand der erste der Reihe. */
const tightest = <T extends CourseAgeRange>(courses: readonly T[]): T =>
  courses.reduce((a, b) => (courseSpan(b) < courseSpan(a) ? b : a));

/** Der passgenaueste Kurs einer Liste — oder `null`, wenn sie leer ist. */
const tightestOrNull = <T extends CourseAgeRange>(courses: readonly T[]): T | null =>
  courses.length > 0 ? tightest(courses) : null;

/** Ergebnis von `chooseCourse`. */
export type CourseChoice<T> =
  /**
   * Ein Kurs steht fest. Passt zusätzlich ein Erwachsenenkurs, steht er in
   * `alternative` — dann dürfen die Eltern wählen, `option` ist die Vorauswahl.
   */
  | { kind: 'auto'; option: T; alternative: T | null }
  /** Das genaue Alter entscheidet — es muss erst abgefragt werden. */
  | { kind: 'needs-age' }
  /** Zum genauen Alter passt kein Kurs dieser Sportart. */
  | { kind: 'no-fit' };

/**
 * Welcher Kurs gehört zu einer Hauptsportart und einer Altersgruppe?
 *
 * `candidates` sind die Kurse **einer** Sportart an **einem** Standort, die für
 * die Gruppe zur Wahl stehen (`isOfferedTo`), in der Reihenfolge der Datenbank
 * (Sortierung, dann Name). Die Reihenfolge entscheidet bei Gleichstand.
 *
 * `exactAge` ist das genaue Alter, sofern schon abgefragt. Es zählt nur dort,
 * wo die Gruppe allein nicht reicht (kein Kurs deckt sie ganz ab).
 *
 * `adultCourses` sind alle Erwachsenenkurse derselben Sportart am Standort —
 * auch die, die für die Gruppe noch nicht zur Wahl stehen (Mindestalter 15 bei
 * der Gruppe 12–15). Sie kommen erst zum Zug, wenn das genaue Alter feststeht
 * und das CRM-Mindestalter es zulässt („MMA bis 14, das Kind ist 15").
 *
 * `group = null` heißt Erwachsene: Es gilt der Kurs mit dem niedrigsten
 * Mindestalter (so lief es schon vorher).
 *
 * Bei mehreren Möglichkeiten geht der Kinderkurs vor; ein passender
 * Erwachsenenkurs kommt als `alternative` mit. Nur in der Gruppe der älteren
 * Jugendlichen (`adults`) gewinnt ein Erwachsenenkurs, der die Gruppe ganz
 * abdeckt, gegen Kinderkurse, die nur einen Teil abdecken.
 */
export function chooseCourse<T extends CourseAgeRange>(
  candidates: readonly T[],
  group: AgeGroup | null,
  exactAge: number | null = null,
  adultCourses: readonly T[] = [],
): CourseChoice<T> | null {
  if (candidates.length === 0) return null;

  if (!group) {
    const first = [...candidates].sort((a, b) => (a.age_min ?? 0) - (b.age_min ?? 0))[0];
    return { kind: 'auto', option: first, alternative: null };
  }

  const kids = candidates.filter((c) => c.for_adults !== true);
  const adults = candidates.filter(isAdultCourseWithMinAge);
  if (kids.length === 0 && adults.length === 0) return null;

  if (exactAge === null) {
    // Deckt mindestens ein Kurs die ganze Gruppe ab, passt er für jedes Kind aus
    // ihr. Der passgenaueste gewinnt („MMA Kids 9–11" vor „MMA Kids + Teens 9–14").
    const kidsCovering = kids.filter((c) => coversGroup(c, group));
    const adultsCovering = adults.filter((c) => coversGroup(c, group));
    if (kidsCovering.length > 0) {
      return { kind: 'auto', option: tightest(kidsCovering), alternative: tightestOrNull(adultsCovering) };
    }
    // Kein Kinderkurs deckt die Gruppe ab, ein Erwachsenenkurs aber schon. Gibt es
    // keine Kinderkurse, die zumindest zu einem Teil der Gruppe passen — oder ist es
    // die Gruppe der älteren Jugendlichen —, steht er fest. Sonst hängt es vom
    // Alter ab, ob Kinder- oder Erwachsenenkurs (KBF Kids bis 13 / KBF Erwachsene ab 12).
    if (adultsCovering.length > 0 && (group.adults === true || kids.length === 0)) {
      return { kind: 'auto', option: tightest(adultsCovering), alternative: null };
    }
    // Nur Teilüberschneidungen: ohne genaues Alter lässt sich nichts sagen.
    return { kind: 'needs-age' };
  }

  // Mit dem genauen Alter zählt allein, ob ein Kind dieses Alters in den Kurs gehört.
  // Erwachsenenkurse auch aus `adultCourses`, sofern das CRM-Mindestalter passt.
  const kidsFitting = kids.filter((c) => containsAge(c, exactAge));
  const adultsFitting = [...new Set([...adults, ...adultCourses.filter(isAdultCourseWithMinAge)])].filter((c) =>
    containsAge(c, exactAge),
  );
  if (kidsFitting.length > 0) {
    return { kind: 'auto', option: tightest(kidsFitting), alternative: tightestOrNull(adultsFitting) };
  }
  if (adultsFitting.length > 0) {
    return { kind: 'auto', option: tightest(adultsFitting), alternative: null };
  }
  return { kind: 'no-fit' };
}
